import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import InfoIcon from '@mui/icons-material/Info';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import 'driver.js/dist/driver.css';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React, { Fragment, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useImmer } from 'use-immer';
import { ClickEvent } from '../../../../common-types';
import { UndefinedDisplayKey } from '../../../common/api/api-interfaces';
import { SparkChartComponent } from '../../../common/components/charts/SparkChartComponent';
import { FixedWidthGap, Flex } from '../../../common/components/layout';
import ServiceExecutorWithRoles from '../../../common/components/query/ServiceExecutorWithRoles';
import { BALANCE_TABLE_ROOT_ELEMENT } from '../../../common/components/share-export/utils';
import {
  getSelectMenuItems,
  InvisibleSelect,
} from '../../../common/components/utils/Select';
import {
  MetricCategoryId,
  MetricGroupId,
  MetricId,
} from '../../../common/graphql/generated/graphql-sdk';
import { getIntercomClassNameForMetric } from '../../../common/intercom/intercom-utils';
import { Service } from '../../../common/services/utils';
import {
  bodyBackground,
  darkTeal,
  mediumGray,
  pbsHeaderBorderCss,
  pbsRowHoverColorLevel1,
  teal2,
  unselectedFactboxColor,
} from '../../../common/theme/color-constants';
import {
  getAliasForMetricCategoryId,
  getAliasForMetricGroupId,
} from '../../../common/v2/api/alias/utils';
import { Dashboards } from '../../../common/v2/common/components/dashboards/types';
import { Filter } from '../../../common/v2/common/components/filter/filterbar/types';
import {
  useAliasesContext,
  useEffectiveRoleViewAuthorizationServiceContext,
  useExecutorRoleViewAuthorizationServiceContext,
  useFeatureFlagsServiceContext,
  useGlobalLocaleContext,
  useIntercomStoreContext,
  useLegacyDomainScopedStoresContext,
} from '../../../common/v2/context/contexts';
import {
  GlobalFilterHandle,
  GlobalSegmentHandle,
} from '../../../common/v2/context/types';
import type { DataFieldWithDataType } from '../../../common/v2/types';
import { BalanceSheetStore } from './balance-sheet-store';
import { PbsSegmentationRows } from './BalanceSheetSegmentationRows';
import { MetricQueryMetaData } from './MetricQueryMetaData';
import { MetricTooltip, PopoverData } from './MetricTooltip';
import { OpenMetricTooltipIcon } from './OpenMetricTooltipIcon';
import { PbsCellDrillDownModal } from './PbsCellDrillDownModal';
import {
  getFormattedValForBenchmarkHeader,
  getFormattedValForFixedHeader,
  getFormattedValForTimeHeader,
  isConfidentialFormattedVal,
} from './services/pbs-utils';
import { TimeFormatToggleIcon } from './TimeFormatToggleIcon';
import {
  getDisplayMetricCategoryName,
  getDisplayMetricGroupName,
  periodTrend,
} from './translations';
import {
  BalanceSheetTableServiceInputs,
  BalanceSheetTableServiceResponse,
  MetaDataObject,
  PbsCategoryDropdownItem,
  PbsCellDrillDownContext,
  PbsServiceResponseData,
  PbsTableHeaderObj,
  PERIOD_TOTAL,
} from './types';
import { usePbsCellDrillDown } from './usePbsCellDrillDown';
import { usePbsDrillDownTour } from './usePbsDrillDownTour';

export interface PbsTableProps {
  balanceSheetStore: BalanceSheetStore;
  dashboard: Dashboards;
  filterHandle: GlobalFilterHandle;
  segmentationLevel1Handle: GlobalSegmentHandle;
  segmentationLevel2Handle: GlobalSegmentHandle;
}

const StyledGrid = styled(Grid)`
  width: 100%;
  height: 100%;
  overflow: auto;
`;

export const StyledSegParentGrid = styled('div')<{
  $isSegmented: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: start;
  padding-left: ${(props) => (props.$isSegmented ? '0.3em' : 0)};
  cursor: pointer;
`;

const StyledTable = styled(Table)`
  width: auto;
  table-layout: fixed;
  height: 100%;
  contain: paint;

  & .MuiTableCell-stickyHeader {
    background-color: ${bodyBackground};
    position: sticky !important;
  }
  /* TODO: cell-widths are not great right now.
  Ideally they need to adapt dynamically acc to the content, or maybe we set colspans. */
`;

const InfoIconContainer = styled(Grid)`
  position: absolute;
  top: 32%;
  right: 0;
  display: none;
  z-index: 1;
`;

const IntercomIcon = styled(InfoIcon)`
  color: ${mediumGray};
  font-size: 1.4em;
  cursor: pointer;
  &:hover {
    color: ${teal2};
  }
`;

export const StyledTableInfoIcon = styled(InfoIcon)`
  color: ${mediumGray};
  font-size: 1.4em;
  cursor: pointer;
  &:hover {
    color: ${teal2};
  }
`;

export const MetaDataIcon = styled(StyledTableInfoIcon)`
  visibility: hidden;
`;

export const StyledTableCell = styled(TableCell)<{
  $color?: string;
  $borderRight?: string;
  $canBeHighlighted?: boolean;
  $isBenchmark?: boolean;
}>`
  color: ${(props) => props.$color ?? 'inherit'};
  border-right: ${(props) => props.$borderRight ?? 'none'};
  border-bottom: none;
  padding: 5px 0.5em 5px 0;
  font-size: 13px;
  position: relative;
  background-color: ${(props) =>
    props.$isBenchmark ? `${darkTeal} !important` : 'unset'};

  &:last-child {
    padding-right: 24px;
  }

  &:not(:empty):hover::after {
    content: '';
    height: 10000px;
    left: 0;
    position: absolute;
    top: -5000px;
    width: 100%;
    z-index: -1;
  }

  &:hover::after {
    background-color: ${(props) =>
      props.$canBeHighlighted ? pbsRowHoverColorLevel1 : 'unset'};
  }
`;

const StyledTypography = styled(Typography)`
  font-weight: 600;
`;

const SubTypography = styled(Typography)`
  cursor: pointer;
  :hover {
    color: ${unselectedFactboxColor};
    font-weight: 600;
  }
`;

export const StyledTableRow = styled(TableRow)`
  height: 0;
  &:hover {
    background-color: ${pbsRowHoverColorLevel1};
    ${InfoIconContainer} {
      display: inline;
    }
    ${MetaDataIcon} {
      visibility: visible;
    }
  }
`;

const StyledIconButton = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  color: ${darkTeal};
  cursor: pointer;
`;

const StyledLink = styled(Link as any)`
  text-decoration: none;
  color: inherit;
  padding-left: 1em;

  :hover {
    color: ${unselectedFactboxColor};
  }
`;

const MeasureHeaderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  min-width: 0;
  gap: 4px;
`;

// Icon rail for the measure cell: the format toggle then the info icon.
// Both children are normalised to the same centred 18px box with a 15px block
// glyph, so the two icons share one centre line regardless of each icon
// component's own inline / baseline quirks, and the toggle keeps a constant-
// width slot (even when a metric can't use it) so the dropdowns stay aligned.
const MeasureHeaderIcons = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: 4px;

  & > * {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 18px;
    height: 18px;
  }

  & svg {
    display: block;
    font-size: 15px;
    margin: 0;
  }
`;

const MeasureHeaderSelect = styled(InvisibleSelect)`
  && {
    min-width: 0;
    flex: 1 1 auto;
  }
`;

export const PBS_DRILLDOWN_CELL_CLASS = 'pbs-drilldown-cell';

export const renderPbsTableCell = (
  metricGroupId: MetricGroupId,
  headerVal: string,
  cellVal: number | string,
  onCellClick?: (context: PbsCellDrillDownContext) => void,
  context?: PbsCellDrillDownContext
) => {
  return (
    <StyledTableCell
      align="right"
      key={`${metricGroupId}${headerVal}${cellVal}`}
      className={onCellClick && context ? PBS_DRILLDOWN_CELL_CLASS : undefined}
      $canBeHighlighted
      onClick={
        onCellClick && context
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onCellClick(context);
            }
          : undefined
      }
      style={onCellClick && context ? { cursor: 'pointer' } : undefined}
    >
      {cellVal}
    </StyledTableCell>
  );
};

export type CellValGetter = (
  dataToUse: PbsServiceResponseData,
  timeHeaderVal: string,
  metricId: MetricId
) => number | null;

export const BalanceSheetTable = observer((props: PbsTableProps) => {
  const aliases = useAliasesContext();
  const locale = useGlobalLocaleContext();
  const selectedLanguage = locale.selected;
  const intercomStore = useIntercomStoreContext();
  const legacyDomainScopedStores = useLegacyDomainScopedStoresContext();
  const authorizationService =
    useEffectiveRoleViewAuthorizationServiceContext();
  const {
    metricStore,
    timeSliderStore,
    benchmarkStore,
    cachedMasterDataApiService,
  } = legacyDomainScopedStores;
  const [metaDataObject, setMetaDataObject] = useState<MetaDataObject | null>(
    null
  );
  const timeHeaderObjsRef = useRef<PbsTableHeaderObj[]>([]);
  const [tableDataReady, setTableDataReady] = useState(false);
  const tableDataReadyScheduledRef = useRef(false);
  const markTableDataReady = () => {
    if (tableDataReadyScheduledRef.current) return;
    tableDataReadyScheduledRef.current = true;
    queueMicrotask(() => {
      setTableDataReady(true);
    });
  };
  const showMetaData = authorizationService.canManageAllDomains();
  const [newPopoverAnchorEl, setNewPopoverAnchorEl] =
    useState<HTMLElement | null>(null);
  const [isNewPopoverOpen, setIsNewPopoverOpen] = useState(false);
  const [currentPopoverDataForNewTooltip, setCurrentPopoverDataForNewTooltip] =
    useState<PopoverData | null>(null);

  const handleOpenNewMetricTooltip = (
    event: React.MouseEvent<HTMLElement>,
    data: PopoverData
  ) => {
    setNewPopoverAnchorEl(event.currentTarget);
    setCurrentPopoverDataForNewTooltip(data);
    setIsNewPopoverOpen(true);
  };

  const handleCloseNewMetricTooltip = () => {
    setNewPopoverAnchorEl(null);
    setCurrentPopoverDataForNewTooltip(null);
    setIsNewPopoverOpen(false);
  };

  const sparkChart = (
    dateValueObjs: Record<string, number>,
    metaDataObject: MetaDataObject | null
  ) => {
    return (
      <StyledTableCell
        style={{ paddingLeft: '1em' }}
        align="right"
        $canBeHighlighted
      >
        <SparkChartComponent
          dateValueObjs={dateValueObjs}
          customWidth={'3vw'}
        />
        {showMetaData && metaDataObject && (
          <Tooltip
            title={'Show Sql'}
            placement="top"
            onClick={(e: ClickEvent) => {
              e.stopPropagation();
              setMetaDataObject(metaDataObject);
            }}
          >
            <MetaDataIcon />
          </Tooltip>
        )}
      </StyledTableCell>
    );
  };
  const { t } = useTranslation();
  const {
    balanceSheetStore,
    dashboard,
    filterHandle,
    segmentationLevel1Handle,
    segmentationLevel2Handle,
  } = props;
  const executorAuthorizationService =
    useExecutorRoleViewAuthorizationServiceContext();
  const canSeeDataExplorer =
    executorAuthorizationService.canSeeDataExplorer(dashboard);
  const canExportDataExplorer =
    executorAuthorizationService.canExportDataExplorer(dashboard);
  const {
    dropdownValues,
    selectedPbsValues,
    segmentedSubVal,
    setSegmentedSubVal,
    setSelectedMeasureOnPbsItem,
    setMetricChart,
    rowInfoLink,
    balanceSheetTableService,
  } = balanceSheetStore;
  const isSegmentationEnabled = true;
  const [segmentationLevel1State] = segmentationLevel1Handle;
  const [segmentationLevel2State] = segmentationLevel2Handle;
  const { items: segmentationLevel1 } = segmentationLevel1State;
  const { items: segmentationLevel2 } = segmentationLevel2State;
  const [filtersState] = filterHandle;
  const { items: filters } = filtersState;
  const { timeSliderConfig } = timeSliderStore;
  const { selectedBenchmark } = benchmarkStore;

  const segmentLevel1Dimension = useMemo(
    () =>
      segmentationLevel1.length > 0
        ? {
            dataType: segmentationLevel1[0].dataType,
            dataField: segmentationLevel1[0].property,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [segmentationLevel1[0]?.dataType, segmentationLevel1[0]?.property]
  );
  const segmentLevel2Dimension = useMemo(
    () =>
      segmentationLevel2.length > 0
        ? {
            dataType: segmentationLevel2[0].dataType,
            dataField: segmentationLevel2[0].property,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [segmentationLevel2[0]?.dataType, segmentationLevel2[0]?.property]
  );

  // Data Explorer (PBS cell drill-down) requires the per-client feature flag and
  // canSeeDataExplorer, which gates on the role's Data View permission (see
  // authorization service). When either is off the handler is left undefined
  // so cells render as non-clickable and the modal cannot open.
  const featureFlagsService = useFeatureFlagsServiceContext();
  const { onCellDrillDown, drillDownModalProps } = usePbsCellDrillDown({
    filters,
    timeSliderConfig,
    metricStore,
    cachedMasterDataApiService,
    segmentLevel1Dimension:
      segmentLevel1Dimension as DataFieldWithDataType | null,
    segmentLevel2Dimension:
      segmentLevel2Dimension as DataFieldWithDataType | null,
    enabled: canSeeDataExplorer && featureFlagsService.isDataExplorerEnabled(),
  });
  usePbsDrillDownTour(tableDataReady, drillDownModalProps.open);

  const getDropdownObjForRowGroup = (rowGroupName: MetricCategoryId) => {
    return dropdownValues.find(
      ({ value }) => rowGroupName === value
    ) as PbsCategoryDropdownItem;
  };

  const MetricCategories = dropdownValues.map((v) => v.value);
  const [rowGroupExpanded, setRowGroupExpanded] = useImmer(
    Object.fromEntries(MetricCategories.map((c) => [c, true]))
  );

  const toggleRowGroupExpanded = (rowGroup: MetricCategoryId) => {
    setRowGroupExpanded((draft) => {
      draft[rowGroup] = !draft[rowGroup];
    });
  };

  const renderPbsTableHeader = (
    { value, label, subVal }: PbsTableHeaderObj,
    isBenchmark = false
  ) => {
    return (
      <StyledTableCell
        key={`${label}${value}${subVal ?? t(UndefinedDisplayKey)}${isBenchmark.toString()}`}
        align="right"
        $color={isBenchmark ? 'white' : darkTeal}
        $borderRight={isBenchmark ? '1px solid white' : pbsHeaderBorderCss}
        $isBenchmark={isBenchmark}
        $canBeHighlighted
      >
        <Grid
          item
          container
          wrap="nowrap"
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'inherit',
            }}
          >
            {label}
          </Typography>
          <FixedWidthGap $gap="4px" />
          {value === PERIOD_TOTAL ? (
            <Tooltip
              title={t(
                `pages:dashboards.peopleBalanceSheet.table.periodTotalTooltip`
              )}
              placement="top"
              onClick={(e: ClickEvent) => {
                e.stopPropagation();
                intercomStore.showArticleInIntercom(value);
              }}
            >
              <StyledTableInfoIcon />
            </Tooltip>
          ) : (
            ''
          )}
        </Grid>
        {subVal ? <Grid item>{subVal}</Grid> : null}
      </StyledTableCell>
    );
  };
  const inputs = {
    timeSliderConfig,
    selectedPbsValues,
    selectedBenchmark,
  };

  return (
    <>
      <MetricTooltip
        open={isNewPopoverOpen}
        anchorEl={newPopoverAnchorEl}
        onClose={handleCloseNewMetricTooltip}
        popoverData={currentPopoverDataForNewTooltip}
      />
      <ServiceExecutorWithRoles
        inputs={toJS(inputs, { recurseEverything: true })}
        filters={toJS(filters) as Filter[]}
        service={
          balanceSheetTableService as unknown as Service<
            BalanceSheetTableServiceInputs,
            BalanceSheetTableServiceResponse
          >
        }
      >
        {({ data }) => {
          if (!data) return null;
          const {
            fixedHeaderObjs,
            timeHeaderObjs,
            benchmarkHeaderObjs,
            tableData,
            meta,
          } = data;
          timeHeaderObjsRef.current = timeHeaderObjs;
          if (timeHeaderObjs.length > 0) {
            markTableDataReady();
          }
          return (
            <StyledGrid id={BALANCE_TABLE_ROOT_ELEMENT}>
              <StyledTable stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledTableCell style={{ width: '15%' }} />
                    <StyledTableCell style={{ width: '10%' }} />
                    {timeHeaderObjs.map((headerObj) =>
                      renderPbsTableHeader(headerObj)
                    )}
                    <StyledTableCell
                      style={{ width: '5%' }}
                      align="right"
                      $color={darkTeal}
                      $borderRight={pbsHeaderBorderCss}
                      $canBeHighlighted
                    >
                      <Grid item>
                        <Typography
                          sx={{
                            color: 'inherit',
                          }}
                        >
                          {periodTrend}
                        </Typography>
                      </Grid>
                    </StyledTableCell>
                    {fixedHeaderObjs.map((headerObj) =>
                      renderPbsTableHeader(headerObj)
                    )}
                    {benchmarkHeaderObjs.map((headerObj) =>
                      renderPbsTableHeader(headerObj, true)
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.rowGroups.map(({ metricCategory, rows }) => {
                    // TODO: Make translation handling better
                    const metricCategoryAlias = getAliasForMetricCategoryId(
                      aliases,
                      selectedLanguage
                    )(metricCategory);
                    const rowGroupLabel = getDisplayMetricCategoryName(
                      metricCategory,
                      metricCategoryAlias,
                      metricStore
                    );
                    const dropdownObj =
                      getDropdownObjForRowGroup(metricCategory);
                    const isExpanded = rowGroupExpanded[metricCategory];
                    const numSelected = dropdownObj.subItems.filter(
                      (sub) => sub.selected
                    ).length;
                    const numTotal = dropdownObj.subItems.length;
                    return (
                      <Fragment key={`balancesheettable-${metricCategory}`}>
                        {
                          <StyledTableRow
                            key={metricCategory}
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              toggleRowGroupExpanded(metricCategory)
                            }
                            data-cy="pbs-table-header-row"
                          >
                            <StyledTableCell
                              component="th"
                              scope="row"
                              $color={darkTeal}
                              style={{ maxWidth: '16em' }}
                            >
                              <Flex>
                                <StyledIconButton>
                                  {isExpanded ? (
                                    <ArrowDropUpIcon />
                                  ) : (
                                    <ArrowDropDownIcon />
                                  )}
                                </StyledIconButton>
                                <StyledTypography color="inherit">{`${rowGroupLabel} (${numSelected}/${numTotal})`}</StyledTypography>
                              </Flex>
                            </StyledTableCell>
                            <StyledTableCell align="right" />
                            {timeHeaderObjs.map(
                              ({ value: headerValue, label }) => {
                                return (
                                  <StyledTableCell
                                    align="right"
                                    $canBeHighlighted
                                    key={`${metricCategory}${headerValue}${label}`}
                                  />
                                );
                              }
                            )}
                            <StyledTableCell
                              style={{ width: '5%' }}
                              align="right"
                              $canBeHighlighted
                            />
                            {[...fixedHeaderObjs, ...benchmarkHeaderObjs].map(
                              ({ value: headerValue, label }) => {
                                return (
                                  <StyledTableCell
                                    align="right"
                                    $canBeHighlighted
                                    key={`${metricCategory}${headerValue}${label}`}
                                  />
                                );
                              }
                            )}
                          </StyledTableRow>
                        }
                        {isExpanded &&
                          rows.map(({ metricGroupId, data }) => {
                            const { metricsToSelectedMetricIdsMap } =
                              balanceSheetStore;
                            const metricGroupIdAlias = getAliasForMetricGroupId(
                              aliases,
                              selectedLanguage
                            )(metricGroupId);
                            const metrics =
                              metricStore.getAllowedSortedMetricIdsForMetricGroup(
                                metricGroupId
                              );
                            const measures = metrics.map((m) => {
                              return {
                                label: metricStore.getTranslatedMetricIdName(
                                  m.id as MetricId
                                ),
                                value: m.id,
                                key: m.id,
                              };
                            });
                            const selectedMeasure =
                              metricsToSelectedMetricIdsMap?.[metricGroupId];
                            if (!selectedMeasure) {
                              throw new Error(
                                `no selected measure for dimension - ${metricGroupId}`
                              );
                            }

                            const selectedMetricDef = metrics.find(
                              (def) => def.id === selectedMeasure
                            );

                            const dateValueObjs: Record<string, number> = {};
                            data[metricGroupId].timeHeadersData.forEach((t) => {
                              const date = t.dateRange[1];
                              const valueForDate = t.data?.[selectedMeasure];
                              dateValueObjs[date] = valueForDate ?? 0;
                            });
                            const currMetaDataObject =
                              meta?.[metricGroupId]?.[selectedMeasure]
                                ?.overtime ?? null;
                            return (
                              <Fragment key={metricGroupId}>
                                <StyledTableRow
                                  onClick={() => {
                                    const canSegment =
                                      !!segmentationLevel1State.items.length &&
                                      isSegmentationEnabled;
                                    if (canSegment) {
                                      segmentedSubVal === metricGroupId
                                        ? setSegmentedSubVal(null)
                                        : setSegmentedSubVal(metricGroupId);
                                    }
                                  }}
                                  style={
                                    !!segmentationLevel1State.items.length &&
                                    isSegmentationEnabled
                                      ? {
                                          cursor: 'pointer',
                                        }
                                      : {}
                                  }
                                  data-cy="pbs-table-value-row"
                                >
                                  <StyledTableCell
                                    component="th"
                                    scope="row"
                                    $color={darkTeal}
                                    style={{ maxWidth: '16rem' }}
                                  >
                                    <StyledSegParentGrid $isSegmented={true}>
                                      {segmentationLevel1State.items.length &&
                                      isSegmentationEnabled ? (
                                        <>
                                          {segmentedSubVal === metricGroupId ? (
                                            <div
                                              onClick={() =>
                                                setSegmentedSubVal(null)
                                              }
                                              style={{ paddingTop: '7px' }}
                                            >
                                              <ArrowDropUpIcon
                                                color="action"
                                                style={{ fontSize: '2.5em' }}
                                                data-cy="pbs-segment-render-arrow-up"
                                              />
                                            </div>
                                          ) : (
                                            <div
                                              onClick={() =>
                                                setSegmentedSubVal(
                                                  metricGroupId
                                                )
                                              }
                                              style={{ paddingTop: '5px' }}
                                            >
                                              <ArrowDropDownIcon
                                                color="action"
                                                style={{ fontSize: '2.5em' }}
                                                data-cy="pbs-segment-render-arrow-down"
                                              />
                                            </div>
                                          )}
                                        </>
                                      ) : null}
                                      <StyledLink
                                        to={rowInfoLink}
                                        onClick={(e: ClickEvent) => {
                                          e.stopPropagation();
                                          setMetricChart({
                                            label: getDisplayMetricGroupName(
                                              metricGroupId,
                                              metricGroupIdAlias,
                                              metricStore
                                            ),
                                            metricGroupId,
                                            metricIds: measures,
                                          });
                                        }}
                                      >
                                        <SubTypography>
                                          {getDisplayMetricGroupName(
                                            metricGroupId,
                                            metricGroupIdAlias,
                                            metricStore
                                          )}{' '}
                                        </SubTypography>
                                      </StyledLink>
                                    </StyledSegParentGrid>
                                    <InfoIconContainer>
                                      {showMetaData && currMetaDataObject && (
                                        <Tooltip
                                          title={'Show Sql'}
                                          placement="top"
                                          onClick={(e: ClickEvent) => {
                                            e.stopPropagation();
                                            setMetaDataObject(
                                              currMetaDataObject
                                            );
                                          }}
                                        >
                                          <MetaDataIcon />
                                        </Tooltip>
                                      )}
                                    </InfoIconContainer>
                                  </StyledTableCell>
                                  <StyledTableCell
                                    align="right"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <MeasureHeaderControls>
                                      <MeasureHeaderSelect
                                        value={selectedMeasure}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          setNewPopoverAnchorEl(null);
                                          setIsNewPopoverOpen(false);
                                          setCurrentPopoverDataForNewTooltip(
                                            null
                                          );
                                          setSelectedMeasureOnPbsItem(
                                            metricGroupId,
                                            e.target.value as MetricId
                                          );
                                        }}
                                        className={getIntercomClassNameForMetric(
                                          selectedMeasure
                                        )}
                                      >
                                        {getSelectMenuItems(
                                          measures.map((m) => {
                                            const intercomClassname = m.value
                                              ? getIntercomClassNameForMetric(
                                                  m.value
                                                )
                                              : '';
                                            return {
                                              ...m,
                                              className: intercomClassname,
                                            };
                                          })
                                        )}
                                      </MeasureHeaderSelect>
                                      <MeasureHeaderIcons>
                                        <TimeFormatToggleIcon
                                          metricId={selectedMeasure}
                                          metricStore={metricStore}
                                          keepSpaceWhenUnavailable
                                        />
                                        <OpenMetricTooltipIcon
                                          metric={{
                                            metricCategoryName:
                                              getDisplayMetricCategoryName(
                                                metricCategory,
                                                metricCategoryAlias,
                                                metricStore
                                              ),
                                            metricGroupName:
                                              getDisplayMetricGroupName(
                                                metricGroupId,
                                                metricGroupIdAlias,
                                                metricStore
                                              ),
                                            metricName:
                                              metricStore.getTranslatedMetricIdName(
                                                selectedMeasure
                                              ),
                                            tooltipJapanese:
                                              selectedMetricDef?.tooltipJapanese ??
                                              null,
                                            tooltipEnglish:
                                              selectedMetricDef?.tooltipEnglish ??
                                              null,
                                          }}
                                          onOpen={handleOpenNewMetricTooltip}
                                        />
                                      </MeasureHeaderIcons>
                                    </MeasureHeaderControls>
                                  </StyledTableCell>
                                  {timeHeaderObjs.map(
                                    ({ value: timeHeaderVal }) => {
                                      const formattedVal =
                                        getFormattedValForTimeHeader(
                                          timeHeaderVal,
                                          selectedMeasure,
                                          data[metricGroupId],
                                          data[metricGroupId],
                                          false,
                                          metricStore
                                        );
                                      const timeHeaderData = data[
                                        metricGroupId
                                      ].timeHeadersData.find(
                                        (d) => d.label === timeHeaderVal
                                      );
                                      const cellContext: PbsCellDrillDownContext =
                                        {
                                          metricGroupId,
                                          selectedMeasure,
                                          headerType: 'time',
                                          headerValue: timeHeaderVal,
                                          dateRange: timeHeaderData?.dateRange,
                                        };
                                      // Confidential cells must never open the Data
                                      // Explorer, or drill-down would recover the
                                      // individual employees the value suppressed.
                                      const isConfidential =
                                        isConfidentialFormattedVal(
                                          formattedVal
                                        );
                                      return renderPbsTableCell(
                                        metricGroupId,
                                        timeHeaderVal,
                                        formattedVal,
                                        onCellDrillDown && !isConfidential
                                          ? (ctx) =>
                                              onCellDrillDown(
                                                ctx,
                                                data[metricGroupId]
                                                  .timeHeadersData
                                              )
                                          : undefined,
                                        cellContext
                                      );
                                    }
                                  )}
                                  {sparkChart(
                                    dateValueObjs,
                                    meta?.[metricGroupId]?.[selectedMeasure]
                                      ?.periodTotal ?? null
                                  )}
                                  {fixedHeaderObjs.map(
                                    ({ value: fixedHeaderVal }) => {
                                      const formattedVal =
                                        getFormattedValForFixedHeader(
                                          fixedHeaderVal,
                                          selectedMeasure,
                                          data[metricGroupId],
                                          data[metricGroupId],
                                          false,
                                          metricStore
                                        );
                                      const cellContext: PbsCellDrillDownContext =
                                        {
                                          metricGroupId,
                                          selectedMeasure,
                                          headerType: 'fixed',
                                          headerValue: fixedHeaderVal,
                                        };
                                      // Confidential cells must never open the Data
                                      // Explorer, or drill-down would recover the
                                      // individual employees the value suppressed.
                                      const isConfidential =
                                        isConfidentialFormattedVal(
                                          formattedVal
                                        );
                                      return renderPbsTableCell(
                                        metricGroupId,
                                        fixedHeaderVal,
                                        formattedVal,
                                        isConfidential
                                          ? undefined
                                          : onCellDrillDown,
                                        cellContext
                                      );
                                    }
                                  )}
                                  {benchmarkHeaderObjs.map(
                                    ({ value: benchmarkHeaderVal }) => {
                                      const cellData =
                                        data[metricGroupId]
                                          .benchmarkHeadersData?.[
                                          benchmarkHeaderVal
                                        ];
                                      if (!cellData) return null;
                                      const formattedVal =
                                        getFormattedValForBenchmarkHeader(
                                          benchmarkHeaderVal,
                                          selectedMeasure,
                                          data[metricGroupId],
                                          data[metricGroupId],
                                          false,
                                          metricStore
                                        );
                                      const currMetaDataObject =
                                        meta?.[metricGroupId]?.[selectedMeasure]
                                          .benchmark ?? null;
                                      return (
                                        <StyledTableCell
                                          align="right"
                                          key={`${benchmarkHeaderVal}${formattedVal}`}
                                          $canBeHighlighted
                                        >
                                          {showMetaData &&
                                            currMetaDataObject && (
                                              <Tooltip
                                                title={'Show Sql'}
                                                placement="top"
                                                onClick={(e: ClickEvent) => {
                                                  e.stopPropagation();
                                                  setMetaDataObject(
                                                    currMetaDataObject
                                                  );
                                                }}
                                              >
                                                <MetaDataIcon />
                                              </Tooltip>
                                            )}
                                          {formattedVal}
                                        </StyledTableCell>
                                      );
                                    }
                                  )}
                                </StyledTableRow>
                                {segmentationLevel1State.items.length &&
                                segmentedSubVal === metricGroupId &&
                                isSegmentationEnabled ? (
                                  <PbsSegmentationRows
                                    metric={metricGroupId}
                                    selectedDimension={selectedMeasure}
                                    balanceSheetStore={balanceSheetStore}
                                    openSqlModal={(
                                      metaDataObject: MetaDataObject
                                    ) => {
                                      setMetaDataObject(metaDataObject);
                                    }}
                                    filterHandle={filterHandle}
                                    segmentationLevel1Handle={
                                      segmentationLevel1Handle
                                    }
                                    segmentationLevel2Handle={
                                      segmentationLevel2Handle
                                    }
                                    onCellDrillDown={onCellDrillDown}
                                  />
                                ) : null}
                              </Fragment>
                            );
                          })}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </StyledTable>
            </StyledGrid>
          );
        }}
      </ServiceExecutorWithRoles>
      <MetricQueryMetaData
        open={!!metaDataObject}
        metaDataObject={metaDataObject}
        onClose={() => setMetaDataObject(null)}
      />
      <PbsCellDrillDownModal
        {...drillDownModalProps}
        canExport={canExportDataExplorer}
        timeHeaderObjs={timeHeaderObjsRef.current}
      />
    </>
  );
});
